/* eslint-disable max-lines-per-function */
import React, { useCallback, useEffect, useState } from 'react';
import Alert from 'components/Alert';
import PaymentPurposeDropdown,
{ PurposeOption } from 'components/Molecules/Payments/CreatePaymentDrawer/PaymentPurposeDropdown/PaymentPurposeDropdown';
import { FormikProps } from 'formik';
import i18n from 'i18n/config';
import { debounce } from 'lodash';
import { Moment } from 'moment';
import { MuiChipsInput } from 'mui-chips-input';
import { useQuery } from 'react-query';
import t from 'utils/translationHelper';

import { CurrencyAccountDto } from '@alpha/currency-accounts-dtos';
import { DrawdownRequest } from '@alpha/fx-dtos';
import { ActionButton } from '@alpha/ui-lib/ui/button';
import { APDatePicker } from '@alpha/ui-lib/ui/DatePicker';
import { Typography } from '@alpha/ui-lib/ui/external';
import { Input } from '@alpha/ui-lib/ui/Input';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faPlusCircle } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import CreateBeneficiary from '../../../../../../domain/Beneficiaries/CreateBeneficiary';
import BeneficiaryDropdownContainer from '../../../../../../domain/Transactions/ManualPayments/Body/PaymentType/ManualPayment/BeneficiaryDropdown/BeneficiaryDropdownContainer';
import Authorization from '../../../../../../hocs/Authorization';
import useAlphaSnackbar from '../../../../../../hooks/useAlphaSnackbar';
import useLog from '../../../../../../hooks/useLog';
import { UserRole } from '../../../../../../models/user';
import PaymentsService from '../../../../../../services/Payments/payments.service';
import InputAndLabel from '../../../../../Inputs/Beneficiary/InputAndLabel';
import PaymentContext from '../../../../Payments/Beneficiaries/AddPayment/paymentContext';
import { FormikPaymentType } from '../../../../Payments/CreatePaymentDrawer/formData';
import { FormikType } from '../../formData';

import useStyles from './index.styles';

type Props = {
  setFieldValue: (field: string, value: any, shouldValidate?: boolean | undefined) => void;
  payments: FormikType;
  drawdownForm?: DrawdownRequest;
  singlePaymentForm: FormikProps<FormikPaymentType>;
  buyCurrencyAccount?: CurrencyAccountDto;
  setDisabled?: React.Dispatch<React.SetStateAction<boolean>>;
};

const AddPayment: React.FC<Props> = ({
  drawdownForm, singlePaymentForm, buyCurrencyAccount, setDisabled,
}) => {
  const styles = useStyles();
  const { logError } = useLog();
  const [beneficiaryDrawerOpen, setBeneficiaryDrawerOpen] = useState<boolean>(false);
  const [showInvoiceFields, setShowInvoiceFields] = useState<boolean>(false);

  const sb = useAlphaSnackbar();
  const availableDateQuery = useQuery('getPaymentDates', () => {
    PaymentsService.getAvailablePaymentDate(
      singlePaymentForm.values.beneficiary?.currencyCode || '',
      drawdownForm?.sellCurrencyCode || '',
    );
  }, {
    enabled: false,
    onError: () => {
      sb.trigger(t('could_not_load_payment_available_date'), 'error');
      logError({ action: 'Could not load payment available date' });
    },
  });
  const paymentPurposesQuery = useQuery('getPaymentPurpose', () => PaymentsService.getPaymentPurpose(
    singlePaymentForm.values.beneficiary?.bankCountryCode || '',
    singlePaymentForm.values.beneficiary?.currencyCode || '',
    singlePaymentForm.values.amount?.toString() || '',
  ), {
    enabled: false,
  });

  useEffect(() => {
    paymentPurposesQuery.remove();
  }, [singlePaymentForm.values.beneficiary]);

  const canBeFreeText = paymentPurposesQuery.data?.canBeFreeText ?? true;

  const debouncedRefetch = useCallback(
    debounce(() => {
      if (singlePaymentForm.values.amount && singlePaymentForm.values.beneficiary) {
        paymentPurposesQuery.refetch();
      }
    }, 800),
    [singlePaymentForm.values.amount, singlePaymentForm.values.beneficiary, paymentPurposesQuery],
  );

  useEffect(() => {
    debouncedRefetch();
  }, [singlePaymentForm.values.amount,
    singlePaymentForm.values.beneficiary,
  ]);

  useEffect(() => {
    // Reset purpose if canBeFreeText changes
    singlePaymentForm.setFieldValue('purpose', undefined, true);
  }, [canBeFreeText]);

  useEffect(() => {
    singlePaymentForm.validateForm();
  }, [singlePaymentForm.values.purpose]);

  const handleDateChange = (_date: Moment) => {
    if (_date) {
      singlePaymentForm.setFieldValue('date', _date.format('YYYY-MM-DD'));
    }
  };

  const handleAmountChange = async (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const amount = parseFloat(event.target.value.replace(/,/g, ''));
    singlePaymentForm.setFieldValue('amount', amount, true);
    singlePaymentForm.validateForm();
  };

  const handleDropdownChange = (event: any, value: PurposeOption) => {
    const selectedValue = value;

    if (paymentPurposesQuery.data) {
      singlePaymentForm.setFieldValue('purpose', selectedValue?.value);

      if (selectedValue?.type === 'Service') {
        setShowInvoiceFields(true);
        singlePaymentForm.setFieldValue('invoiceDate', singlePaymentForm.values.date, true);
        singlePaymentForm.setFieldValue('purposeType', 'Service');
        singlePaymentForm.setFieldTouched('invoiceDate', true, true);
      } else {
        setShowInvoiceFields(false);
        singlePaymentForm.setFieldValue('purposeType', 'Unspecified');
        singlePaymentForm.setFieldValue('invoiceNumber', null);
        singlePaymentForm.setFieldValue('invoiceDate', null, false);
      }
    }
  };

  const handleInvoiceDateChange = (_date: Moment) => {
    if (_date) {
      singlePaymentForm.setFieldValue('invoiceDate', _date.format('YYYY-MM-DD'), true);
    }
  };

  const handleInvoiceNumberChange = (e: any) => {
    singlePaymentForm.setFieldValue('invoiceNumber', e.target.value, true);
  };

  useEffect(() => {
    if (singlePaymentForm.values.beneficiary?.currencyCode) {
      availableDateQuery.refetch();
      paymentPurposesQuery.refetch();
      singlePaymentForm.setFieldValue('date', drawdownForm?.valueDate);
    }
  }, [singlePaymentForm.values.beneficiary]);

  useEffect(() => {
    singlePaymentForm.setFieldValue('date', drawdownForm?.valueDate);
  }, [drawdownForm?.valueDate]);

  const purposeOfPaymentValidated = (
    isRequired: boolean,
    paymentPurpose?: string,
    shouldShowInvoiceFields?: boolean,
    invoiceNumber?:string, invoiceDate?:string,
  ) => {
    if (!isRequired) {
      setDisabled?.(false);
      return true;
    }

    if (!shouldShowInvoiceFields) {
      const valid = !!(isRequired && paymentPurpose);
      setDisabled?.(!valid);
      return valid;
    }
    const valid = !!(isRequired && paymentPurpose && invoiceNumber && invoiceDate);
    setDisabled?.(!valid);
    return valid;
  };

  const disabled = !singlePaymentForm.isValid || !purposeOfPaymentValidated(
    paymentPurposesQuery.data?.required ?? false,
    singlePaymentForm.values.purpose,
    showInvoiceFields,
    singlePaymentForm.values.invoiceNumber,
    singlePaymentForm.values.invoiceDate,
  );

  return (
    <form className={styles.container} onSubmit={singlePaymentForm.handleSubmit}>
      <div style={{ marginBottom: '20px' }}>
        <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
          <Typography variant="subtitle1">{t('beneficiary')}</Typography>
          <Authorization requiredRoles={[[UserRole.BENEFICIARY_INPUTTER]]}>
            <Typography
              variant="subtitle1"
              style={{ color: '#1E8777', textDecoration: 'underline', cursor: 'pointer' }}
              onClick={() => setBeneficiaryDrawerOpen(true)}
            >
              {t('create_new?')}
            </Typography>
          </Authorization>
        </div>
        <BeneficiaryDropdownContainer
          currencyCode={drawdownForm?.buyCurrencyCode || ''}
          beneficiary={singlePaymentForm.values.beneficiary}
          setFieldValue={singlePaymentForm.setFieldValue}
        />
      </div>
      <InputAndLabel
        label={`${t('amount_currency')} ${drawdownForm?.buyCurrencyCode}`}
        name="amount"
        id="amount"
        type="amount"
        testId="furtherToAccountNumber"
        onChange={handleAmountChange}
        onBlur={singlePaymentForm.handleBlur}
        value={singlePaymentForm.values.amount}
        error={
          singlePaymentForm.touched.amount
          && Boolean(singlePaymentForm.errors.amount)
        }
        helperText={
          singlePaymentForm.touched.amount
          && singlePaymentForm.errors.amount
        }
      />
      <div style={{ marginTop: '20px', width: '100%' }}>
        <APDatePicker
          fullWidth
          label={t('payment_date')}
          language={i18n.language}
          className="datePicker"
          onChange={handleDateChange}
          value={singlePaymentForm.values.date}
          disabled
          placeholder={t('payment_date~')}
          emptyLabel={t('select_date')}
          autoOk
        />
      </div>
      <div style={{ marginTop: '20px', width: '100%', marginBottom: '20px' }}>
        <InputAndLabel
          label={t('reference_(drawdown)')}
          name="reference"
          id="reference"
          testId="reference"
          onChange={singlePaymentForm.handleChange}
          onBlur={singlePaymentForm.handleBlur}
          value={singlePaymentForm.values.reference}
          error={
            singlePaymentForm.touched.reference
            && Boolean(singlePaymentForm.errors.reference)
          }
          helperText={
            singlePaymentForm.touched.reference
            && singlePaymentForm.errors.reference
          }
        />
      </div>
      <div style={{ marginBottom: '20px' }}>
        <Typography variant="subtitle1" className="label">
          {t('purpose_of_payment')}
        </Typography>
        {!canBeFreeText ? (
          <PaymentPurposeDropdown
            data={paymentPurposesQuery.data ? paymentPurposesQuery.data?.purposes : []}
            handleChange={handleDropdownChange}
            placeholderText={t('please_select_a_purpose_of_payment')}
            data-testid="purpose"
            selectName="purpose"
          />
        ) : (
          <Input
            onChange={singlePaymentForm.handleChange}
            fullWidth
            id="purpose"
            variant="filled"
            value={singlePaymentForm.values.purpose && typeof singlePaymentForm.values.purpose === 'string' ? singlePaymentForm.values.purpose : ''}
            testId="purposeOfPayment-input"
            helperText={singlePaymentForm.errors.purpose}
            error={!!singlePaymentForm.errors.purpose}
          />
        )}
      </div>
      {paymentPurposesQuery.data?.required && !singlePaymentForm.values.purpose && (
        <div style={{ width: '100%' }}>
          <Alert
            className={styles.info}
            variant="info"
            text={t('purpose_of_payment_is_required')}
          />
        </div>
      )}

      {showInvoiceFields && (
      <div style={{ marginTop: '20px', width: '100%', marginBottom: '20px' }}>
        <InputAndLabel
          label={t('invoice_number')}
          name="invoiceNumber"
          id="invoiceNumber"
          testId="invoiceNumber"
          onChange={handleInvoiceNumberChange}
          onBlur={singlePaymentForm.handleBlur}
          value={singlePaymentForm.values.invoiceNumber}
          error={
                      singlePaymentForm.touched.invoiceNumber
                      && Boolean(singlePaymentForm.errors.invoiceNumber)
                    }
          helperText={
                      singlePaymentForm.touched.invoiceNumber
                      && singlePaymentForm.errors.invoiceNumber
                    }
        />
      </div>
      )}
      {showInvoiceFields && (
      <div style={{ marginTop: '20px', width: '100%' }}>
        <APDatePicker
          fullWidth
          language={i18n.language}
          label={t('invoice_date')}
          InputLabelProps={{
            shrink: true,
          }}
          className="datePicker"
          onChange={handleInvoiceDateChange}
          value={singlePaymentForm.values.invoiceDate}
          placeholder={t('invoice_date')}
        />
      </div>
      )}
      <div style={{ marginTop: '20px' }}>
        <Typography variant="subtitle1">{t('optional_email')}</Typography>
        <MuiChipsInput
          data-testid="bene-email-addresses"
          className={styles.chip}
          value={singlePaymentForm.values?.emailAddresses ?? []}
          fullWidth
          onChange={(chips: string[]) => {
            singlePaymentForm.setFieldValue('emailAddresses', chips);
          }}
          error={Boolean(singlePaymentForm.errors.emailAddresses)}
          helperText={
            singlePaymentForm.errors.emailAddresses?.length
              ? singlePaymentForm.errors.emailAddresses : ''
          }
          placeholder={t('enter_save_email')}
        />
      </div>
      <PaymentContext.Provider value={{
        currencyCode: buyCurrencyAccount!.currencyCode,
        currency: buyCurrencyAccount!.currency,
        ...singlePaymentForm,
      }}
      >
        <CreateBeneficiary
          open={beneficiaryDrawerOpen}
          header="Create Drawdown / New Beneficiary"
          handleDrawerClose={() => setBeneficiaryDrawerOpen(false)}
        />
      </PaymentContext.Provider>
    </form>
  );
};

export default AddPayment;
